'use client';

import { Button } from '@/components/ui/button';
import { Link2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { AUTHOR_NAME, SITE_TITLE_PLAIN } from '@/lib/site-config';
import { SPACING } from '@/lib/design-tokens';

interface PostQuickActionsProps {
  postTitle?: string;
  publishedAt?: string;
}

/**
 * Post Quick Actions Section
 *
 * Copy link and copy IEEE citation. Like, bookmark and share live in the
 * PostInteractions row under the article, so they are not repeated here.
 */
export function PostQuickActions({ postTitle, publishedAt }: PostQuickActionsProps) {
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link copied to clipboard!');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleCopyLink = async () => {
    await copyToClipboard(window.location.href);
  };

  const generateIEEECitation = (): string => {
    const url = window.location.href;
    const date = publishedAt ? new Date(publishedAt) : new Date();
    const formattedDate = date.toLocaleDateString('en-US', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    // IEEE format: [#] Initial(s). Surname, "Article title," Website Name, Month Day, Year. [Online]. Available: URL. [Accessed: Month Day, Year].
    const citation = `[1] ${AUTHOR_NAME}, "${postTitle}," ${SITE_TITLE_PLAIN}, ${formattedDate}. [Online]. Available: ${url}. [Accessed: ${new Date().toLocaleDateString('en-US', { timeZone: 'UTC', year: 'numeric', month: 'long', day: 'numeric' })}].`;

    return citation;
  };

  const handleCopyIEEECitation = async () => {
    try {
      const citation = generateIEEECitation();
      await navigator.clipboard.writeText(citation);
      toast.success('Citation copied to clipboard!');
    } catch {
      toast.error('Failed to copy citation');
    }
  };

  return (
    <div className={`${SPACING.compact} pb-6 border-b`}>
      <h2 className="font-semibold mb-3 text-sm">Quick Actions</h2>

      {/* Copy Link Button */}
      <Button
        variant="outline"
        size="sm"
        className="w-full justify-start gap-2"
        onClick={handleCopyLink}
      >
        <Link2 className="h-4 w-4" />
        Copy Link
      </Button>

      {/* Citation Button */}
      <Button
        variant="outline"
        size="sm"
        className="w-full justify-start gap-2"
        onClick={handleCopyIEEECitation}
        title="Copy webpage citation"
      >
        <BookOpen className="h-4 w-4" />
        Copy Citation
      </Button>
    </div>
  );
}
